import { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import type { OpticlockEntryInput, OpticlockEntryPatchInput } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { AgentSelect, ME, type AgentOption } from '../components/AgentSelect';
import { EntryForm } from '../components/EntryForm';
import { EntryTable } from '../components/EntryTable';
import { MonthBreakdown } from '../components/MonthBreakdown';
import { MonthSelect } from '../components/MonthSelect';
import {
  useAddOpticlockEntry,
  useAdminDeleteOpticlockEntry,
  useAdminUpdateOpticlockEntry,
  useDeleteOpticlockEntry,
  useOpticlockMonth,
  useOpticlockProjectPicker,
  useUpdateOpticlockEntry,
} from '../hooks/useOpticlock';
import { useOpticlockAgentTotals } from '../hooks/useOpticlockDashboard';
import { currentMonthCentral } from '../lib/month';
import { splitByProject } from '../lib/split';

/**
 * My time — the agent's entry page (root + support).
 *
 * ROOT'S AGENT SELECTOR is what gives root's cross-agent edit a home without adding a page.
 * Switching it re-points this same table and its edit/delete controls at that agent's rows,
 * routed through the root-only mutations (which confirm the row's owner server-side and never
 * reassign it). Support never sees the control, and the backend would refuse it anyway.
 *
 * The selector's options come from the dashboard rollup — i.e. agents who actually logged time
 * this month. Root edits existing entries, so those are exactly the agents worth offering, and
 * it avoids a second query for a staff directory.
 */
export function TimeTrackerPage() {
  const me = useAuthStore((s) => s.adminUser);
  const isRoot = !!me?.roles?.includes(ADMIN_ROLES.ROOT);

  const [month, setMonth] = useState(currentMonthCentral());
  const [agent, setAgent] = useState(ME);
  const viewingOther = isRoot && agent !== ME;
  const viewedAgent = viewingOther ? agent : undefined;

  const projectsQ = useOpticlockProjectPicker();
  const projects = projectsQ.data?.opticlockProjects ?? [];

  const { month: data, loading, error } = useOpticlockMonth(month, viewedAgent);

  // Root only, and ONLY agents who actually logged time in the selected month.
  //
  // The staff roster was tried and dropped on request: 79 admin_users rows, almost all of whom
  // have never touched OptiClock, buried the handful of names worth looking at. Picking someone
  // with an empty month only ever showed an empty month, so the roster added length without
  // adding an answer. Consequence to know: an agent who logged nothing this month cannot be
  // selected — switch months to reach them.
  const totalsQ = useOpticlockAgentTotals(month);
  const agentOptions: AgentOption[] = useMemo(() => {
    if (!isRoot) return [];
    return (totalsQ.data?.opticlockAgentMonthTotals ?? [])
      .map((a) => ({ email: a.agentEmail, minutes: a.minutes }))
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [isRoot, totalsQ.data]);

  const [addEntry, addState] = useAddOpticlockEntry();
  const [updateOwn] = useUpdateOpticlockEntry();
  const [deleteOwn] = useDeleteOpticlockEntry();
  const [adminUpdate] = useAdminUpdateOpticlockEntry();
  const [adminDelete] = useAdminDeleteOpticlockEntry();

  async function handleAdd(input: OpticlockEntryInput): Promise<void> {
    try {
      await addEntry({ variables: { input } });
      toast.success('Time logged');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not log that entry');
    }
  }

  async function handleUpdate(id: string, patch: OpticlockEntryPatchInput): Promise<void> {
    try {
      if (viewingOther) {
        await adminUpdate({ variables: { id, agentEmail: agent, patch } });
      } else {
        await updateOwn({ variables: { id, patch } });
      }
      toast.success('Entry updated');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update that entry');
    }
  }

  async function handleDelete(id: string): Promise<void> {
    try {
      if (viewingOther) {
        await adminDelete({ variables: { id, agentEmail: agent } });
      } else {
        await deleteOwn({ variables: { id } });
      }
      toast.success('Entry deleted');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not delete that entry');
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="opticlock-page">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
        <Clock className="h-6 w-6" />
        {viewingOther ? `Time Tracker — ${agent}` : 'Time Tracker'}
      </h1>
      <p className="max-w-3xl text-xs text-muted-foreground">
        {
          'Log the time you spend on work that is not a ticket, against the categories root maintains. Totals reset per month — nothing is ever deleted.'
        }
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <MonthSelect value={month} onChange={setMonth} testId="opticlock-month" />

        {isRoot ? (
          <AgentSelect
            value={agent}
            onChange={setAgent}
            agents={agentOptions}
            loading={totalsQ.loading}
          />
        ) : null}
      </div>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="opticlock-error"
        >
          {error.message}
        </p>
      ) : null}

      {/* Root looking at someone else's month is REVIEWING it, not logging on their behalf —
          so the add form is hidden. Correcting an existing row is what root asked for; filing
          a new one as another person is not. */}
      {viewingOther ? null : (
        <EntryForm projects={projects} adding={addState.loading} onAdd={handleAdd} />
      )}

      {/* The agent's own split, shown outright rather than behind a hover: the person reading
          this is the person who logged the time, and "where did my month go" is the question
          the page exists to answer. Folded from the entries already on screen — no extra query,
          so it can never disagree with the list below it. */}
      <MonthBreakdown
        totalMinutes={data?.totalMinutes ?? 0}
        split={splitByProject(data?.entries ?? [])}
      />

      <EntryTable
        entries={data?.entries ?? []}
        projects={projects}
        loading={loading}
        totalMinutes={data?.totalMinutes ?? 0}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </div>
  );
}
